import { useMutation, useQuery, useQueryClient } from 'react-query';
import AIInteractionAPI, {
  ScoutChatItem,
  ScoutAgentChatResponse,
  Session,
} from 'services/API/AIInteractionAPI';

const SCOUT_CHATS_QUERY_KEY = ['scoutAgent', 'chats'] as const;
export const SCOUT_CHAT_BY_ID_QUERY_KEY = (id: string) =>
  ['scoutAgent', 'chat', id] as const;

/** Fetch all Scout Agent chats. Mock data for now; API call commented out. */
export const useAllScoutAgentChats = () => {
  return useQuery(
    SCOUT_CHATS_QUERY_KEY,
    async (): Promise<ScoutChatItem[]> => {
      const response = await AIInteractionAPI.getAllScoutAgentChats();

      return response?.data?.sessions ?? [];
    },
    { refetchOnWindowFocus: false },
  );
};

/** Fetch a single Scout Agent chat by id. Mock data for now; API call commented out. */
export const useScoutAgentChatById = (chatId: string | null) => {
  return useQuery(
    SCOUT_CHAT_BY_ID_QUERY_KEY(chatId ?? ''),
    async (): Promise<Session | null> => {
      if (!chatId) return null;

      const response = await AIInteractionAPI.getScoutAgentChatById(chatId);

      return response?.data ?? null;
    },
    {
      enabled: !!chatId && !chatId.includes('new'),
      refetchOnWindowFocus: false,
      // A brand-new chat's session id only exists once its first response
      // has already arrived, at which point the caller seeds this exact
      // query key via setQueryData with the complete (user + agent) history
      // before this query is ever enabled. The default refetch-on-mount
      // would otherwise immediately race that seed against a real GET for a
      // session the backend may not have finished persisting yet, and can
      // clobber the correct seeded data with an incomplete read. Explicit
      // invalidateQueries calls (useSendScoutAgentChat's onSuccess) still
      // force a refetch when it's actually needed.
      refetchOnMount: false,
    },
  );
};

type SendScoutAgentChatVariables = {
  message: string;
  sessionId: string;
};

/** Send a message in an existing Scout Agent chat. Uses react-query mutation; invalidates chat on success. */
export const useSendScoutAgentChat = () => {
  const queryClient = useQueryClient();

  return useMutation<ScoutAgentChatResponse, Error, SendScoutAgentChatVariables>({
    mutationFn: async ({ message, sessionId }) => {
      const result = await AIInteractionAPI.postScoutAgentChat({ message, sessionId });

      return result.data;
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries(SCOUT_CHAT_BY_ID_QUERY_KEY(sessionId));
    },
  });
};

/** Create a new Scout Agent chat with an initial message. Invalidates chats list on success. */
export const useCreateScoutAgentChat = () => {
  const queryClient = useQueryClient();

  return useMutation<ScoutAgentChatResponse, Error, string>({
    mutationFn: async (message) => {
      const result = await AIInteractionAPI.postScoutAgentNewChat({ message });

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(SCOUT_CHATS_QUERY_KEY);
    },
  });
};
